import { useEffect, useState } from 'react';
import { PALETTE, type Channel, type CueItem, type Presenter } from '@micplot/shared';
import { api } from '../lib/api';
import { useStore } from '../state/store';

interface Props {
  cue: CueItem;
  /** Which assignment state within the cue is being edited. */
  stepId: string;
  channels: Channel[];
  presenters: Presenter[];
  onClose: () => void;
}

/**
 * Set who is on which mic for one cue.
 *
 * Laid out per channel rather than per presenter, because the rig is the fixed
 * thing: an operator looks down the list of transmitters and says who is on
 * each, not the other way round.
 */
export default function CueEditor({ cue, stepId, channels, presenters, onClose }: Props) {
  const step = cue.steps.find(st => st.id === stepId) ?? cue.steps[0]!;
  const stepIndex = cue.steps.findIndex(st => st.id === step.id);
  const identity = useStore(s => s.identity)!;
  const notify = useStore(s => s.notify);

  const [map, setMap] = useState<Record<string, string>>(
    Object.fromEntries(step.assignments.map(a => [a.channelId, a.presenterId])),
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function save() {
    setSaving(true);
    try {
      const assignments = Object.entries(map)
        .filter(([, presenterId]) => presenterId)
        .map(([channelId, presenterId]) => ({ channelId, presenterId }));
      await api.setStepAssignments(identity.code, step.id, assignments);
      onClose();
    } catch (err) {
      notify((err as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const used = new Set(Object.values(map).filter(Boolean));

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-label={`Assignments for ${cue.label}`}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>
            {cue.label}
            {cue.steps.length > 1 && (
              <span className="muted" style={{ fontWeight: 400, fontSize: 14 }}>
                {' '}· change {stepIndex + 1} of {cue.steps.length}
                {step.label ? ` — ${step.label}` : ''}
              </span>
            )}
          </h2>
          <button className="btn sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <p className="sub">
          Who is on each mic for this cue. Landing here puts exactly this on the board —
          anyone not listed comes off.
        </p>

        {channels.length === 0 ? (
          <p className="muted">Add some channels first.</p>
        ) : (
          <div style={{ display: 'grid', gap: 7 }}>
            {channels.map(ch => {
              const chosen = map[ch.id] ?? '';
              const p = presenters.find(x => x.id === chosen);
              return (
                <div className="row" key={ch.id}>
                  <span className="ch-num" style={{ width: 52 }}>TX {ch.number}</span>
                  <span className="chip">{ch.micType}</span>
                  {p && (
                    <span className="swatch"
                          style={{ background: PALETTE[p.color % PALETTE.length] }} />
                  )}
                  <select
                    value={chosen}
                    aria-label={`Presenter on TX ${ch.number}`}
                    onChange={e => setMap({ ...map, [ch.id]: e.target.value })}
                  >
                    <option value="">— empty —</option>
                    {presenters.map(x => (
                      <option key={x.id} value={x.id}
                              disabled={used.has(x.id) && x.id !== chosen}>
                        {x.name}{x.characterName ? ` (${x.characterName})` : ''}
                        {x.micPreference !== ch.micType ? ` — wants ${x.micPreference}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn" onClick={() => setMap({})}>Clear all</button>
          <div className="header-spacer" />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
